import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Star, ExternalLink, Loader2, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Lazy-fetched Google Reviews block for the campsite detail page.
 *
 * - Collapsed by default — does NOT call Google until the user clicks
 *   "Show Google reviews" (truly on-demand → near-zero quota cost).
 * - Backend caches top-5 reviews in MongoDB for 30 days.
 * - Renders nothing if the feature is disabled (no API key on server)
 *   or if Google has no reviews for this site.
 *
 * The pill at the top of the page already shows the aggregate; this is
 * the deeper-dive expansion for users who want to read what other
 * campers said.
 */
export default function GoogleReviewsTab({ campsiteId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!campsiteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api(`/api/campsites/${encodeURIComponent(campsiteId)}/places-reviews`);
      setData(res);
    } catch (e) {
      setError("Couldn't load Google reviews right now.");
    } finally {
      setLoading(false);
    }
  }, [campsiteId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading) load();
  };

  // Pre-flight: if Place Summary has already told us the feature is
  // disabled or there are no Google results, we don't bother showing the
  // expand button at all. We piggyback on the pill's existing cache hit
  // by doing a HEAD-style summary check on first mount.
  const [eligible, setEligible] = useState(null); // null=unknown, true/false
  useEffect(() => {
    let cancel = false;
    if (!campsiteId) return undefined;
    (async () => {
      try {
        const summary = await api(`/api/campsites/${encodeURIComponent(campsiteId)}/places-summary`);
        if (cancel) return;
        if (!summary || summary.enabled === false || summary.zero_results) {
          setEligible(false);
          return;
        }
        const reviewCount = summary.user_ratings_total || 0;
        setEligible(reviewCount > 0);
      } catch {
        if (!cancel) setEligible(false);
      }
    })();
    return () => { cancel = true; };
  }, [campsiteId]);

  // Eligibility unknown or known-false → render nothing.
  if (eligible !== true) return null;

  return (
    <div className="mt-10" data-testid="google-reviews-section">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 border hairline-strong bg-white hover:bg-[#fafafa] transition-colors text-left"
        data-testid="google-reviews-toggle"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
          <span className="font-display tracking-tight normal-case text-lg">
            <span className="text-[#4285F4]">G</span>
            <span className="text-[#EA4335]">o</span>
            <span className="text-[#FBBC04]">o</span>
            <span className="text-[#4285F4]">g</span>
            <span className="text-[#34A853]">l</span>
            <span className="text-[#EA4335]">e</span>
          </span>
          <span className="text-[#1f1f1f]">Reviews</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-3 p-4 border hairline bg-white" data-testid="google-reviews-panel">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#666] mono" data-testid="google-reviews-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading reviews from Google…
            </div>
          )}
          {!loading && error && (
            <div className="text-sm text-[#9a3412] mono" data-testid="google-reviews-error">{error}</div>
          )}
          {!loading && data && data.reviews && data.reviews.length === 0 && (
            <div className="text-sm text-[#666]" data-testid="google-reviews-empty">
              No Google reviews on file for this campsite yet.
            </div>
          )}
          {!loading && data && data.reviews && data.reviews.length > 0 && (
            <ul className="space-y-5" data-testid="google-reviews-list">
              {data.reviews.map((rv, i) => (
                <li key={i} className="flex gap-3" data-testid={`google-review-${i}`}>
                  {rv.profile_photo_url ? (
                    <img
                      src={rv.profile_photo_url}
                      alt={rv.author_name || "Reviewer"}
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0 bg-[#eee]"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#eee] flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {rv.author_url ? (
                        <a
                          href={rv.author_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-semibold hover:underline truncate"
                        >
                          {rv.author_name || "Google user"}
                        </a>
                      ) : (
                        <span className="text-sm font-semibold truncate">
                          {rv.author_name || "Google user"}
                        </span>
                      )}
                      {typeof rv.rating === "number" && (
                        <span className="inline-flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, j) => (
                            <Star
                              key={j}
                              className={`w-3 h-3 ${j < rv.rating ? "fill-[#fbbc04] text-[#fbbc04]" : "text-[#dadce0]"}`}
                              strokeWidth={1.5}
                            />
                          ))}
                        </span>
                      )}
                      {rv.relative_time_description && (
                        <span className="text-[11px] mono text-[#888]">
                          · {rv.relative_time_description}
                        </span>
                      )}
                    </div>
                    {rv.text && (
                      <p className="text-sm text-[#333] mt-1 whitespace-pre-line">
                        {rv.text}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {!loading && data && data.google_maps_url && (
            <a
              href={data.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-5 text-[11px] mono text-[#5f6368] hover:text-[#1f1f1f]"
              data-testid="google-reviews-maps-link"
            >
              See all reviews on Google Maps <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
